<template>
  <v-container fluid id="inbound-quotas" v-if="quotaInformationMapped">
    <v-row v-if="hasQuotaInformation && !loading">
      <v-col cols="12" v-if="contractAccountsWithQuota">
        <v-select
          v-if="quotaInformationMapped"
          v-model="destinationFilter"
          :items="destinationNamesForSelector"
          :label="$t('destination')"
          :menu-props="{ bottom: true, offsetY: true }"
          item-value="id"
          item-text="name"
          color="primary"
          return-object
          style="width: 300px; max-width: 300px;"
          @change="mapAndFilterQuotaInformation(false)"
        />
      </v-col>
      <v-col cols="12" :class="contractAccountsWithQuota ? 'py-0' : ''">
        <span class="title">{{ currentPeriodString }}</span>
      </v-col>
      <v-col cols="12" class="py-0">
        <v-progress-linear rounded indeterminate class="mb-n4" v-if="loading"></v-progress-linear>
      </v-col>
      <v-col cols="12" lg="" class="fill-width pt-8" style="min-width: 0; max-height: calc(100vh - 368px); overflow: auto;" v-if="contractAccountsWithQuota">
        <v-expansion-panels accordion tile v-model="openPanel" active-class="whiteStroke" class="secondaryStroke" id="contract-account-expansion-panels">
          <v-expansion-panel v-for="account in quotaInformationMapped.contractAccounts" :key="account.name" :disabled="!account.hasQuota">
            <v-expansion-panel-header :style="$vuetify.breakpoint.xs ? '' : 'height: 64px'" id="contract-account-expansion-panel-header">
              <v-container fluid class="pl-0">
                <v-row align="center" no-gutters>
                  <v-col cols="12" sm="" class="pr-2">
                    <v-row align="center" dense>
                      <v-col cols="auto">
                        <Icon
                          icon="mdi-account"
                          :iconColor="helpers.getTranslucent(60)"
                          :tooltipText="$t('contractAccount')"
                          tooltipColor="black"
                          :small="false"
                        />
                      </v-col>
                      <v-col>
                        {{ account.name }}
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="auto" :style="!$vuetify.breakpoint.xs ? { width: '180px' } : {}" class="body-2 py-1">
                    <v-row no-gutters>
                      <v-col cols="12">
                        <v-row no-gutters>
                          <v-col>
                            <span >{{ $t('created') }}</span>
                          </v-col>
                          <v-col cols="auto" class="pl-2">
                            {{ account.loadsCreated }}
                          </v-col>
                        </v-row>
                        <v-row no-gutters>
                          <v-col>
                            <span>{{ $t('allocated') }}</span>
                          </v-col>
                          <v-col cols="auto" class="pl-2">
                            {{ account.quotaOnAccount }}
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="auto" style="width: 40px;">
                    <v-tooltip bottom :color="percentageWheelToolTipColor(account)">
                      <template #activator="{ on }">
                        <v-progress-circular
                        :rotate="-90"
                        class="ml-2"
                        width="14"
                        size="40"
                        :color="percentageWheelColor(account)"
                        :value="account.loadsCreated / account.quotaOnAccount * 100 || 0"
                        v-on="on"
                        />
                      </template>
                      <span class="subtitle-1 white--text">{{ percentageWheelToolTipText(account) }}</span>
                    </v-tooltip>
                  </v-col>
                </v-row>
              </v-container>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-container fluid>
                <v-tabs class="card-tabs" v-model="account.tab" touchless v-if="!destinationFilterApplied">
                  <v-tab v-for="destination in account.destinations" :key="destination.destinationAccountName">
                    <v-row align="center" dense>
                      <v-col cols="auto">
                        <Icon
                          icon="mdi-domain"
                          :iconColor="helpers.getTranslucent(60)"
                          :tooltipText="$t('destinationAccount')"
                          tooltipColor="black"
                          :small="false"
                        />
                      </v-col>
                      <v-col>
                        {{ destination.name }}
                      </v-col>
                    </v-row>
                  </v-tab>
                </v-tabs>
                <v-card style="border-radius: 0px 4px 4px 4px" :outlined="!destinationFilterApplied" :flat="destinationFilterApplied">
                  <v-window v-model="account.tab">
                    <v-window-item v-for="destination in account.destinations" :key="destination.destinationAccountName">
                      <v-col cols="12">
                        <DataTable
                          v-if="account.destinations && columnSet"
                          :items.sync="destination.contractInfo"
                          :headers="columnSet"
                          :customCells="customContractColumns"
                          :actions="tableActions"
                          @refresh="refreshQuotas(false)"
                          @click:row="onContractRowClicked"
                          showPointerCursor
                        >
                          <template #status="{item}">
                            <Icon
                              v-if="!loading"
                              :iconColor="getStatusIcon(item).color"
                              margin="mx-1"
                              dataTestId="contract-detail-status"
                              :icon="getStatusIcon(item).iconName"
                              :small="false"
                              :tooltipText="getStatusIcon(item).msg"
                            />
                          </template>
                          <template #created="{item}">
                            <span class="mr-2">{{item.quotaContractCount || 0}}</span>
                          </template>
                          <template #related="{item}">
                            <Icon
                              :small="false"
                              icon="mdi-information-outline"
                              margin=""
                              :tooltipText="$t('contractDetails')"
                              @icon-clicked="openContractDetail(item.contractId)"
                            />
                          </template>
                          <template #actions="{item}">
                            <td @click.stop.prevent class="text-center cursor-default">
                              <Icon
                                v-if="item.paused"
                                icon="mdi-pause-circle"
                                iconColor="black"
                                margin=""
                                :small="false"
                                :tooltipText="$t('unpauseContract')"
                                @icon-clicked="handleContractState(item.contractId, false)"
                              />
                              <Icon
                                v-else
                                icon="mdi-pause-circle-outline"
                                iconColor="black"
                                margin=""
                                :small="false"
                                :tooltipText="$t('pauseContract')"
                                @icon-clicked="handleContractState(item.contractId, true)"
                              />
                            </td>
                          </template>
                        </DataTable>
                      </v-col>
                    </v-window-item>
                  </v-window>
                </v-card>
              </v-container>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
      <v-col cols="12" :lg="contractAccountsWithQuota ? 4 : 12" :xl="contractAccountsWithQuota ? 3 : 12" id="destination-table">
        <DataTable
          v-if="quotaInformationMapped.destinationAccounts && quotaColumnSet"
          :items.sync="quotaInformationMapped.destinationAccounts"
          :headers="quotaColumnSet"
          :customCells="customQuotaColumns"
          :actions="tableActions"
          :showSearch="false"
          @click:row="onQuotaRowClicked"
          showPointerCursor
          @refresh="refreshQuotas(false)"
        >
          <template #name="{item}">
            <v-row align="center" class="flex-nowrap" no-gutters>
              <v-col cols="auto">
                <Icon
                  icon="mdi-domain"
                  iconColor="black"
                  :tooltipText="$t('destinationAccount')"
                  :small="false"
                  @icon-clicked="showQuotaDetail(item)"
                />
              </v-col>
              <v-col cols="auto">
                <span>
                  {{item.name || $t('notAvailable')}}
                </span>
              </v-col>
              <v-col>
                <Icon
                  v-if="item.quotaInfoForContractAccounts.some(ca => ca.quota == null) && item.hasQuota"
                  iconColor="warning"
                  margin="mx-1"
                  icon="mdi-exclamation"
                  :small="false"
                  :tooltipText="$t('missingAtLeastOneQuota')"
                  @icon-clicked="showQuotaDetail(item)"
                />
              </v-col>
            </v-row>
          </template>
          <template #allocated="{item}">
            <span class="mr-2">{{item.quotaAssignedToDestination || ''}}</span>
          </template>
          <template #created="{item}">
            <v-tooltip bottom :color="percentageBarToolTipColor(item)" v-if="item.hasQuota">
              <template #activator="{ on }">
                <v-progress-linear
                  :value="item.loadsCreatedOnDestination / item.quotaAssignedToDestination * 100 || 0"
                  v-on="on"
                  height="16"
                  rounded
                  :color="percentageBarColor(item)"
                  background-color="rgba(0,0,0,0.1)"
                  style="box-shadow: inset 0px 0px 0px 1px rgba(0,0,0,0.1);"
                />
              </template>
              <p class="subtitle-1 white--text my-0">{{ `${item.loadsCreatedOnDestination} ${$t('created')}\n` }}</p>
              <span class="subtitle-1 white--text">{{ `${item.quotaAssignedToDestination} ${$t('allocated')}` }}</span>
            </v-tooltip>
              <Icon
                v-else
                iconColor="warning"
                margin="mx-1"
                icon="mdi-alert"
                :small="false"
                :tooltipText="$t('noQuotasDestinationAccount')"
                @icon-clicked="showQuotaDetail(item)"
              />
            </template>
          </DataTable>
        </v-col>
      </v-row>
      <NoData v-else-if="!loading">
        {{ $t('noAccountsThatIncurQuota') }}
      </NoData>
      <v-row align="center" justify="center" style="height: 100vh;" v-else>
        <v-progress-circular indeterminate color="primary" />
      </v-row>
      <Dialog :stateId="dialogId"
        maxWidth="80vw"
        @click:outside="closeDialogs()"
        @dialog-closing="closeDialogs()"
        @close="closeDialogs()"
        :enforcePersistence="editingQuotas"
      >
        <ContractDetail v-if="contractDetail" :contractId="chosenContractId"/>
        <QuotaDetail v-if="quotaDetail" :contractAccounts="chosenQuotaContractAccounts" :destination="chosenDestination" @refresh="refreshQuotas" @close="closeDialogs()" @editing="changeEditStatus"/>
      </Dialog>
    </v-container>
</template>

<script>
import { uniqueDialogId } from '@/utils/componentHelpers'
import quotaHeaders from '@/headers/Quota.js'
import { ContractStatusReason } from '@/utils/Enumerations.js'
import { ContractStatus } from '../../utils/Enumerations'
import { convertToPercentage } from '@/utils/NumericMutations.js'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { helpers } from '../../plugins/theme'
import { formatDate } from '../../utils/DateFormatter'

export default {
  name: 'InboundQuota',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ContractDetail: () => import('@/components/contract/contract-detail/ContractDetail.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    QuotaDetail: () => import('@/components/quota/QuotaDetail.vue'),
    NoData: () => import('@/components/core/NoData.vue')
  },

  data: () => ({
    openPanel: undefined,
    quotaInformationMapped: {},
    dialogId: uniqueDialogId('quotas'),
    columnSet: null,
    quotaColumnSet: null,
    chosenContractId: undefined,
    contractDetail: false,
    quotaDetail: false,
    editingQuotas: false,
    destinationFilter: undefined,
    chosenQuotaContractAccounts: [],
    chosenDestination: undefined,
    helpers
  }),

  watch: {
    'quotaInformationMapped.destinationAccounts': {
      handler (val) {
        if (this.quotaDetail) {
          this.chosenQuotaContractAccounts = val.find(da => da.accountId === this.chosenDestination.id)?.quotaInfoForContractAccounts
        }
      },
      deep: true
    },
    'quotaInformationMapped.contractAccounts': {
      handler (val) {
        if (val.length === 1) {
          this.$nextTick(_ => {
            this.openPanel = 0
          })
        }
      }
    }
  },

  computed: {
    ...mapGetters('quotas', ['loading', 'quotaInformation']),

    quotaContractHeaders () {
      return quotaHeaders.quotaContractHeaders()
    },

    currentPeriodString () {
      return this.$t('quotaPeriodHeader', { x: formatDate(this.quotaInformationMapped.startOfQuotaPeriod, 'MMMM D'), y: formatDate(this.quotaInformationMapped.endOfQuotaPeriod, 'MMMM D') })
    },

    contractAccountsWithQuota () {
      return this.quotaInformationMapped.contractAccounts?.some(a => a.hasQuota)
    },

    quotaDestinationHeaders () {
      return quotaHeaders.quotaDestinationHeaders()
    },

    hasQuotaInformation () {
      return this.quotaInformation.contractAccounts?.length > 0 || this.quotaInformation.destinationAccounts?.length > 0
    },

    destinationFilterApplied () {
      return this.destinationFilter?.id !== 0
    },

    destinationNamesForSelector () {
      const destinations = this.quotaInformation?.destinationAccounts?.filter(da => da.quotaInfoForContractAccounts?.some(ca => ca.quota?.quotaInEffect !== null && ca.quota !== null))
        .map(a => ({
          id: a.accountId,
          name: a.name
        }))
      if (destinations && destinations.length > 1) {
        destinations.unshift({
          id: 0,
          name: this.$t('allDestinations')
        })
      }
      return destinations
    },
    customContractColumns () {
      return [
        {
          slotName: 'status',
          value: 'status'
        },
        {
          slotName: 'created',
          value: 'quotaContractCount'
        },
        {
          slotName: 'related',
          value: 'related'
        },
        {
          slotName: 'actions',
          value: 'actions'
        }
      ]
    },
    customQuotaColumns () {
      return [
        {
          slotName: 'name',
          value: 'name'
        },
        {
          slotName: 'allocated',
          value: 'quotaAssignedToDestination'
        },
        {
          slotName: 'created',
          value: 'quotaAssignedCreated'
        }
      ]
    },
    tableActions () {
      return [
        {
          actionName: 'refresh',
          icon: 'mdi-refresh',
          text: this.$t('refresh'),
          id: 'refresh'
        }
      ]
    }
  },

  async created () {
    await this.refreshQuotas()
    this.resetDestinationFilter()
    this.columnSet = this.quotaContractHeaders.getColumns()
    this.quotaColumnSet = this.quotaDestinationHeaders.getColumns()
    this.setAllowEscapeToClose(true)
  },

  methods: {
    ...mapMutations('dialog', ['setAllowEscapeToClose']),
    ...mapActions('quotas', ['fetchQuotaInformation']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('contract', ['getContract', 'updateContract']),
    convertToPercentage,

    async refreshQuotas (resetPanel = true) {
      await this.fetchQuotaInformation()
      this.mapAndFilterQuotaInformation(resetPanel)
    },

    changeEditStatus (editStatus) {
      this.editingQuotas = editStatus
      this.setAllowEscapeToClose(!editStatus)
    },

    mapQuotaInformation () {
      const quotaInfo = { ...this.quotaInformation }
      this.quotaInformationMapped = { ...this.quotaInformation }
      this.quotaInformationMapped.contractAccounts = quotaInfo.contractAccounts.map(ca => ({
        ...ca,
        tab: 0
      })).filter(a => a.hasQuota)
      this.quotaInformationMapped.destinationAccounts = quotaInfo.destinationAccounts.map(da => ({
        ...da,
        quotaAssignedCreated: {
          assigned: da.quotaAssignedToDestination,
          deliverd: da.loadsCreatedOnDestination
        }
      }))
    },

    mapAndFilterQuotaInformation (resetPanel) {
      this.mapQuotaInformation()
      if (resetPanel) this.openPanel = undefined
      if (this.destinationNamesForSelector.length === 1) this.resetDestinationFilter()
      if (this.destinationFilter !== undefined && this.destinationFilter.id !== 0 && this.destinationNamesForSelector.some(d => d.id === this.destinationFilter.id)) {
        this.quotaInformationMapped.contractAccounts = this.quotaInformationMapped.contractAccounts.filter(a => a.destinations.some(d => d.accountId === this.destinationFilter.id) && a.hasQuota)
        this.quotaInformationMapped.contractAccounts.forEach(a => {
          a.destinations = a.destinations?.filter(d => d.accountId === this.destinationFilter.id)
          a.quotaOnAccount = a.quotaForDestinations.find(d => d.accountId === this.destinationFilter.id)?.quotaOnAccount
          a.loadsCreated = a.quotaForDestinations.find(d => d.accountId === this.destinationFilter.id)?.loadsCreated
        })
      } else {
        this.resetDestinationFilter()
      }
    },

    resetDestinationFilter () {
      this.destinationFilter = this.destinationNamesForSelector[0]
    },

    async handleContractState (contractId, paused) {
      const contract = await this.getContract(contractId)
      contract.paused = paused
      contract.ticketBookIds = contract.ticketBooks?.map(tb => tb.ticketBookId) ?? []
      if (contract.fromAccountId === 0) contract.fromAccountId = null
      await this.updateContract(contract)
      await this.refreshQuotas(false)
    },

    percentageWheelToolTipText (account) {
      if (account.quotaOnAccount === 0) {
        if (account.loadsCreated === 0) {
          return '100%'
        } else {
          return this.$t('overQuota')
        }
      }
      if (!account.quotaOnAccount) return this.$t('noQuotaSet')
      else if (account.loadsCreated / account.quotaOnAccount > 1) return this.$t('overQuota')
      else return convertToPercentage(account.loadsCreated / account.quotaOnAccount, 0, true)
    },

    percentageWheelColor (account) {
      if (account.loadsCreated === account.quotaOnAccount) return 'warning'
      else if (account.loadsCreated > account.quotaOnAccount) return 'error'
      else return 'success'
    },

    percentageWheelToolTipColor (account) {
      if (account.loadsCreated === account.quotaOnAccount) return 'warning'
      else if (account.loadsCreated > account.quotaOnAccount && account.quotaOnAccount !== 0) return 'error'
      else return 'black'
    },

    percentageBarColor (item) {
      if (item.loadsCreatedOnDestination === item.quotaAssignedToDestination) return 'warning'
      else if (item.loadsCreatedOnDestination < item.quotaAssignedToDestination) return 'success'
      else return 'error'
    },

    percentageBarToolTipColor (item) {
      if (item.loadsCreatedOnDestination === item.quotaAssignedToDestination) return 'warning'
      else if (item.loadsCreatedOnDestination < item.quotaAssignedToDestination) return 'black'
      else return 'error'
    },

    openContractDetail (contractId) {
      this.chosenContractId = contractId
      this.closeDialogs()
      this.contractDetail = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    },

    showQuotaDetail (information) {
      this.chosenQuotaContractAccounts = information.quotaInfoForContractAccounts
      this.chosenDestination = {
        name: information.name,
        id: information.accountId,
        quotaPeriodDate: `${formatDate(information.startOfNextQuotaPeriod, 'MMMM D')} - ${formatDate(information.endOfNextQuotaPeriod, 'MMMM D')}`
      }
      this.closeDialogs()
      this.quotaDetail = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '60vw' })
    },

    closeDialogs () {
      this.contractDetail = false
      this.quotaDetail = false
      this.editingQuotas = false
      this.setAllowEscapeToClose(true)
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    getStatusIcon (contract) {
      const status = contract.status
      let statusReason = ContractStatusReason.fromInt(contract.statusReason)
      if (contract.statusReason !== ContractStatusReason.None.value) {
        statusReason = `${statusReason}`
      } else {
        statusReason = ''
      }
      switch (status) {
        case ContractStatus.Open.value:
          return {
            iconName: 'mdi-checkbox-marked-circle',
            color: 'success',
            msg: this.$t('open')
          }
        case ContractStatus.Closed.value:
          return {
            iconName: 'mdi-close',
            color: 'error',
            msg: `${this.$t('closed')}
                  ${statusReason}`
          }
        case ContractStatus.OnHold.value:
          return {
            iconName: 'mdi-minus-circle',
            color: 'warning',
            msg: `${this.$t('onHold')}
                  ${statusReason}`
          }
        case ContractStatus.Expired.value:
          return {
            iconName: 'mdi-close-circle',
            color: 'error',
            msg: `${this.$t('expired')}
                  ${statusReason}`
          }
        default:
          return {
            iconName: 'mdi-alert',
            color: 'error',
            msg: this.$t('exception')
          }
      }
    },

    onContractRowClicked ({ data }) {
      this.openContractDetail(data.contractId)
    },

    onQuotaRowClicked ({ data }) {
      this.showQuotaDetail(data)
    }
  }
}
</script>
